import { c as _c } from "react/compiler-runtime";
import type { FootprintVariant } from '@onefootprint/footprint-js-legacy';
import type { OnboardingSessionConfig } from '@onefootprint/request-types';
import { cx } from 'class-variance-authority';
import type React from 'react';
import { type CSSProperties, useCallback, useEffect, useState } from 'react';
import { IDV_BODY_CONTENT_CONTAINER_ID, LEGACY_IDV_CONTAINER_ID as LAYOUT_CONTAINER_ID } from '@/idv2/components/constants';
import FootprintFooter from '../footprint-footer';
import { useLayoutOptions } from '../layout-options-provider';
import NavigationHeaderContainer from '../navigation-header/components/navigation-header-container';
import type { SandboxBannerHandler } from '../sandbox-banner';
import SandboxBanner from '../sandbox-banner';
import { BOTTOM_ACTION_BOX_PORTAL_ID } from '../sticky-bottom-box/constants';
import { fieldNavigationHandlers } from './field-navigation';
type ContentProps = {
  children: React.ReactNode;
  variant?: FootprintVariant;
  isSandbox?: boolean;
  isHosted?: boolean;
  config?: OnboardingSessionConfig;
  fitContent?: boolean;
  headerRef?: React.Ref<HTMLDivElement>;
};
const Content = t0 => {
  const $ = _c(44);
  const {
    children,
    variant,
    isSandbox,
    isHosted,
    config,
    fitContent: t1,
    headerRef
  } = t0;
  const fitContent = t1 === undefined ? false : t1;
  const {
    options,
    footer
  } = useLayoutOptions();
  let t2;
  if ($[0] !== options) {
    t2 = options || {};
    $[0] = options;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    hideDesktopSandboxBanner,
    hideDesktopFooter
  } = t2;
  const [sandboxBannerHeight, setSandboxBannerHeight] = useState(0);
  const {
    options: t3
  } = footer;
  const {
    height: footerHeight
  } = t3;
  const [bodyContentOverflowing, setBodyContentOverflowing] = useState(false);
  const showSandboxBanner = isSandbox && (config?.sandboxConfig?.showSandboxBanner || isHosted);
  const showFooter = !config?.flags?.hideFootprintFooter || isHosted;
  let t4;
  let t5;
  if ($[2] !== fitContent) {
    t4 = () => {
      if (fitContent) {
        setBodyContentOverflowing(false);
        return;
      }
      const checkOverflow = () => {
        const layoutContainer = document.getElementById(LAYOUT_CONTAINER_ID);
        if (!layoutContainer) {
          return;
        }
        const isOverflowing = layoutContainer.scrollHeight > layoutContainer.clientHeight;
        setBodyContentOverflowing(isOverflowing);
      };
      const resizeObserver = new ResizeObserver(checkOverflow);
      const body = document.querySelector(`#${IDV_BODY_CONTENT_CONTAINER_ID}`);
      if (body) {
        resizeObserver.observe(body);
      }
      return () => {
        if (body) {
          resizeObserver.unobserve(body);
        }
      };
    };
    t5 = [fitContent];
    $[2] = fitContent;
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useEffect(t4, t5);
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = handler => {
      if (!handler) {
        return;
      }
      const height = handler.getHeight();
      if (height) {
        setSandboxBannerHeight(height);
      }
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const measuredRef = t6;
  const t7 = fitContent ? "grow" : "min-h-full shrink-0";
  let t8;
  if ($[6] !== t7) {
    t8 = cx("flex flex-col relative", t7);
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== hideDesktopSandboxBanner || $[9] !== showSandboxBanner) {
    t9 = showSandboxBanner && <SandboxBanner ref={measuredRef} hideOnDesktop={hideDesktopSandboxBanner} />;
    $[8] = hideDesktopSandboxBanner;
    $[9] = showSandboxBanner;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== sandboxBannerHeight) {
    t10 = <NavigationHeaderContainer top={sandboxBannerHeight} containerId={LAYOUT_CONTAINER_ID} />;
    $[11] = sandboxBannerHeight;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== headerRef || $[14] !== t10 || $[15] !== t9) {
    t11 = <div ref={headerRef} className="sticky top-0 z-sticky flex flex-col">{t9}{t10}</div>;
    $[13] = headerRef;
    $[14] = t10;
    $[15] = t9;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const t12 = variant === "inline" && "min-[900px]:mx-auto min-[900px]:w-full min-[900px]:[max-width:var(--bifrost-container-max-width,_none)]";
  let t13;
  if ($[17] !== t12) {
    t13 = cx("flex flex-col relative flex-grow", t12);
    $[17] = t12;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  const t14 = fitContent ? "overflow-y-visible" : "overflow-y-auto";
  let t15;
  if ($[19] !== t14) {
    t15 = cx("flex flex-col box-content p-4 h-full overflow-x-hidden md:px-6 md:pb-6 md:pt-0", t14);
    $[19] = t14;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== children || $[22] !== t15) {
    t16 = <div className={t15}>{children}</div>;
    $[21] = children;
    $[22] = t15;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] !== t13 || $[25] !== t16) {
    t17 = <div className={t13} id={IDV_BODY_CONTENT_CONTAINER_ID}>{t16}</div>;
    $[24] = t13;
    $[25] = t16;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  const t18 = bodyContentOverflowing && "border-t border-solid border-tertiary [&:empty]:border-none";
  let t19;
  if ($[27] !== t18) {
    t19 = cx("flex flex-col sticky z-[5] bottom-[env(safe-area-inset-bottom)] min-[900px]:bottom-[var(--footer-height)]", t18);
    $[27] = t18;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  const t20 = `${footerHeight}px`;
  let t21;
  if ($[29] !== t20) {
    t21 = {
      "--footer-height": t20
    };
    $[29] = t20;
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  const t22 = t21 as CSSProperties;
  let t23;
  if ($[31] !== t19 || $[32] !== t22) {
    t23 = <div id={BOTTOM_ACTION_BOX_PORTAL_ID} className={t19} style={t22} />;
    $[31] = t19;
    $[32] = t22;
    $[33] = t23;
  } else {
    t23 = $[33];
  }
  let t24;
  if ($[34] !== config || $[35] !== hideDesktopFooter || $[36] !== showFooter) {
    t24 = showFooter && <FootprintFooter hideOnDesktop={hideDesktopFooter} config={config} />;
    $[34] = config;
    $[35] = hideDesktopFooter;
    $[36] = showFooter;
    $[37] = t24;
  } else {
    t24 = $[37];
  }
  let t25;
  if ($[38] !== t11 || $[39] !== t17 || $[40] !== t23 || $[41] !== t24 || $[42] !== t8) {
    t25 = <div className={t8} {...fieldNavigationHandlers}>{t11}{t17}{t23}{t24}</div>;
    $[38] = t11;
    $[39] = t17;
    $[40] = t23;
    $[41] = t24;
    $[42] = t8;
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  return t25;
};
export default Content;